<template>
  <div>
    <v-dialog
      v-model="show"
      persistent
      scrollable
      max-width="800"
    >
      <v-card>
        <v-card-title class="headline">Skapa leverantörskonto</v-card-title>
        <div class="font-italic pl-6 mb-3">
          Kul att ni vill använda våra tjänster! <br>
          Fyll i uppgifterna i de olika stegen så är du snart igång. <br>
          Har du frågor och funderingar är det bara att du hör av dig. 
        </div>
        <v-card-text>

  <v-stepper v-model="step" ref="accountstepper">
    <v-stepper-header>
      <v-stepper-step :complete="step > 1" step="1">Företagsinformation</v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step :complete="step > 2" step="2">Företagspresentation</v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step step="3">Inloggning</v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content step="1">
        <v-card
          flat
          min-height="200px"
        >
          <v-form v-model="companyValid">
            <v-text-field 
              v-model="model.company.name"
              :rules="requiredRules"
              required
              label="Företagsnamn *"></v-text-field>
            <v-text-field 
              v-model="model.company.orgnr"
              :rules="requiredRules"
              required
              label="Org. nummer *"></v-text-field>
            <v-text-field 
              v-model="model.company.address"
              :rules="requiredRules"
              required
              label="Adress *"></v-text-field>
            <v-text-field 
              v-model="model.company.post_address"
              :rules="requiredRules"
              required
              label="Postadress *"></v-text-field>
            <v-text-field 
              v-model="model.company.phone"
              label="Telefon"></v-text-field>
            <v-text-field 
              v-model="model.company.email"
              label="Epost till företaget"></v-text-field>
          </v-form>
        </v-card>
      </v-stepper-content>

      <v-stepper-content step="2">
        <v-card
          flat
          min-height="200px"
        >
          <v-form v-model="companyDescriptionValid">
            <v-textarea
              v-model="model.company.description"
              label="Presentation av ditt företag"
            ></v-textarea>
          </v-form>
        </v-card>
      </v-stepper-content>

      <v-stepper-content step="3">
        <v-card
          flat
          min-height="200px"
        >
          <v-form v-model="contactValid">
            <v-text-field 
              v-model="model.name"
              :rules="requiredRules"
              required
              label="Kontaktnamn *"></v-text-field>
            <v-text-field 
              v-model="model.email"
              :rules="emailRules"
              required
              label="E-postadress *"></v-text-field>
            <p class="text-error" v-if="emailExistsError">E-postadressen finns redan</p>
            <v-text-field 
              v-model="model.password"
              :rules="passwordRules"
              required
              type="password"
              label="Lösenord *"></v-text-field>

            <v-checkbox v-model="model.accepted_license" :rules="requiredRules">
              <template v-slot:label>
                <div>
                  Ja, jag godkänner
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <a
                        @click.stop.prevent="showLicenseDialog=true"
                        v-on="on"
                      >
                        licensvillkoren
                      </a>
                    </template>
                    Läs licensvillkoren här
                  </v-tooltip>
                </div>
              </template>
            </v-checkbox>

          </v-form>
        </v-card>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>

          <p class="text-error" v-if="showSignupFailedError">Ett fel uppstod. Kontrollera att alla fält är ifyllda och försök igen.</p>
        </v-card-text>
        
        <v-divider></v-divider>
        <v-card-actions class="pr-4 pb-3">
          <v-spacer></v-spacer>

          <v-btn
            text
            @click="cancel()">
            Avbryt
          </v-btn>

          <v-btn
            text
            :disabled="isFirstStep"
            @click="prevStep()">
            Föregående
          </v-btn>

          <v-btn
            color="primary"
            class="black--text"
            :disabled="!isFormValid()"
            v-if="!isLastStep()"
            @click="nextStep()">
            Nästa >>
          </v-btn>

          <v-btn
            color="primary"
            class="black--text"
            :disabled="!isFormValid()"
            :loading="creatingAccount"
            v-if="isLastStep()"
            @click="signup()">
            Skapa konto
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <policy-dialog-component
        :visible="showPolicyDialog"
        @close="policyClosed()"></policy-dialog-component>

    <license-info-dialog-component :visible="showLicenseDialog" :vendor="true" @close="showLicenseDialog=false"></license-info-dialog-component>
  </div>
</template>

<script>
import { UserService } from '../../services/user.service';
import LicenseInfoDialogComponent from './LicenseInfoDialogComponent.vue';
import PolicyDialogComponent from './PolicyDialogComponent.vue';

export default {
  components: {
    PolicyDialogComponent,
    LicenseInfoDialogComponent
  },

  props: {
    visible: Boolean,
    default: true
  },

  model: {
    prop: "visible",
    event: "update"
  },

  data() {
    return {
      step: 1,
      model: { company: { pay_plan_id: '1' } },
      showPolicyDialog: false,
      showLicenseDialog: false,
      showSignupFailedError: false,
      emailExistsError: false,
      companyValid: false,
      companyDescriptionValid: false,
      contactValid: false,
      creatingAccount: false,

      //rules
      requiredRules: [
        v => !!v || 'Obligatoriskt'
      ],
      passwordRules: [
        v => !!v && v.length >= 5 || 'Obligatoriskt och minst 5 tecken'
      ],
      emailRules: [
        v => !!v || 'Obligatoriskt',
        v => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v) || 'Ogiltig epostadress'
      ]
    };
  },

  computed: {
    show: {
      get: function() {
        return this.visible;
      },
      set: function(value) {
        this.$emit("update", value);

        if (value === false) {
          this.$emit("close");
        }
      }
    },

    isFirstStep: function() {
      return this.step === 1;
    },
  },

  methods: {
    numberOfSteps() {
      return this.$refs.accountstepper ? this.$refs.accountstepper.steps.length : 1;
    },
    isLastStep() {
      return this.step === this.numberOfSteps();
    },

    signup() {
      if (this.creatingAccount) {
        return;
      }
      
      this.creatingAccount = true;
      new UserService()
        .signupVendor(this.model)
        .then(() => {
          this.cancel();
          this.$router.push({ name: 'Dashboard' });
        })
        .catch(err => {
          const error = err && err.response ? err.response.data : {};
          if (error.type === 'validation_error' && !!error.error.email) {
            this.emailExistsError = true;
          }
          this.showSignupFailedError = true;
          this.creatingAccount = false;
        });
    },

    cancel() {
      this.clearFields();
      this.step = 1;
      this.showSignupFailedError = false;
      this.creatingAccount = false;
      this.show = false;
    },

    clearFields() {
      this.model = { company: { pay_plan_id: '1' } };
    },

    nextStep() {
      this.step += 1;
      if (this.step >= this.numberOfSteps()) {
        this.step = this.numberOfSteps();
      }
    },

    prevStep() {
      this.step -= 1;
      if (this.step <= 0) {
        this.step = 1;
      }
    },

    isFormValid() {
      if (this.step === 1)
        return this.companyValid;
      if (this.step === 2)
        return this.companyDescriptionValid
        
      return this.companyValid && this.companyDescriptionValid && this.contactValid;
    },

    showPolicy() {
      this.showPolicyDialog = true;
    },

    policyClosed() {
      this.showPolicyDialog = false;
    }
  }
};
</script>

<style scoped>
  .radio-label {
    color: black;
    font-weight: bold;
  }
  .radio-sub-label {
    color: #999;
    font-size: .8em;
    margin-left: 35px!important;
  }
</style>
